import { DateTime } from 'luxon'

import {
  permanentDstLabel,
  resolveDisplayZone,
  resolveWallClockZone,
  zoneAbbreviation,
} from './permanent-dst'

/**
 * The luxon counterpart to `dayjs-permanent-dst`, for zones whose abolished
 * clock change the viewer's timezone database has not caught up with.
 *
 * dayjs could be corrected once, as a plugin on a singleton. Luxon has no such
 * seam — `DateTime` is imported directly and `setZone` is called on instances —
 * so each entry point has to opt in, and these are the two calls to reach for
 * when one does. `ts-utils/permanent-dst` holds the rules and decides when
 * nothing needs doing; on a runtime that already knows the real rule both of
 * these stand aside and return what luxon would have.
 *
 * Two directions, matching the two the rules module distinguishes:
 *
 * - `instantInZone` — reading a stored instant to display it. The instant
 *   picks the rules.
 * - `wallClockInZone` — carrying a typed wall clock into a zone before
 *   publishing it. The typed clock picks the rules; getting this one wrong
 *   writes the hour into the database rather than merely drawing it.
 */

/**
 * Reads an instant in `timeZone`, on the offset that zone actually keeps.
 *
 * @param iso - the stored instant
 * @param timeZone - the real IANA zone to read it in
 */
export const instantInZone = (iso: string, timeZone: string): DateTime => {
  const asInstant = DateTime.fromISO(iso)
  if (!asInstant.isValid) {
    return asInstant
  }
  return asInstant.setZone(resolveDisplayZone(timeZone, asInstant.toMillis()))
}

/**
 * Carries a typed wall clock into `timeZone`, on the offset that zone actually
 * keeps — luxon's `setZone(zone, { keepLocalTime: true })`.
 *
 * @param iso - the wall clock the picker produced
 * @param timeZone - the real IANA zone it was typed for
 */
export const wallClockInZone = (iso: string, timeZone: string): DateTime => {
  const typed = DateTime.fromISO(iso)
  if (!typed.isValid) {
    return typed
  }
  //  The typed reading measured as though it were UTC, which is what the rules
  //  module compares against its wall-clock changeover.
  const asUtcMs = Date.UTC(
    typed.year,
    typed.month - 1,
    typed.day,
    typed.hour,
    typed.minute,
    typed.second,
    typed.millisecond
  )
  return typed.setZone(resolveWallClockZone(timeZone, asUtcMs), {
    keepLocalTime: true,
  })
}

/**
 * Formats `value`, putting back the label the zone's residents use.
 *
 * The substituted zone is right about the offset but not about its name — IANA
 * names a permanent summer offset after the standard zone that shares it — so
 * a format asking for a zone name would otherwise say 'MST' for a BC game.
 * A no-op unless the pattern actually rendered one.
 *
 * @param value - the already zone-corrected DateTime
 * @param realTimeZone - the zone the caller asked for, before substitution
 * @param pattern - a luxon format string
 */
export const formatWithZoneLabel = (
  value: DateTime,
  realTimeZone: string,
  pattern: string
): string => {
  const formatted = value.toFormat(pattern)
  if (!value.isValid) {
    return formatted
  }

  const label = permanentDstLabel(realTimeZone, value.toMillis())
  if (label === null) {
    return formatted
  }

  const abbreviation = zoneAbbreviation(value.zoneName ?? '', value.toMillis())
  return abbreviation === '' || abbreviation === label
    ? formatted
    : formatted.split(abbreviation).join(label)
}
